import React, { useState, useEffect } from "react";
import { Button, message, Steps, Modal, Spin } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import JobFormStepOne from "./PostNewJob/JobFormStepOne";
import BasicDetails from "./PostNewJob/BasicDetails";
import WorkPreferenceForm from "./PostNewJob/WorkPreferenceForm";
import { PostNewJobWrapper } from "./PostNewJob.style";
import { API_CONST } from "../../../const";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";

const PostNewJob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  const isEditMode = Boolean(jobId);
  const fromApprovalProcess = location.state?.from === 'approval-process';
  const [current, setCurrent] = useState(0);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidBasicDetails, setIsValidBasicDetails] = useState(false);
  const [isValidWorkPreference, setIsValidWorkPreference] = useState(false);
  const [isValidJobDescription, setIsValidJobDescription] = useState(false);
  const [customerOrganizations, setCustomerOrganizations] = useState([]);

  const [formData, setFormData] = useState({
    customerOrganization: "",
    engagementId: null,
    projectId: null,
    role: "",
    experienceRange: "",
    primarySkills: [null, null, null],
    secondarySkills: [],
    budget: null,
    budgetPeriod: "Per/month",
    location: "",
    worldwide: false,
    engagementMonths: null,
    engagementType: null,
    requirementCount: null,
    requirementPeriod: "Per/month",
    startDate: null,
    communication: null,
    timezone: null,
    workingHours: null,
    travel: null,
    device: null,
    tools: [],
    shiftTiming: null,
    shiftStart: null,
    shiftEnd: null,
    responsibilities: "",
  });

  useEffect(() => {
    const fetchCustomerOrganizations = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(API_CONST.GET_CUSTOMER_ORGANIZATIONS, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomerOrganizations(response.data.organizations || []);
      } catch (error) {
        console.error("Error fetching customer organizations:", error);
      }
    };
    fetchCustomerOrganizations();
  }, []);

  useEffect(() => {
    const fetchJobData = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.job) {
          const job = response.data.job;
          setFormData({
            customerOrganization: job.customerOrganization || "",
            engagementId: job.engagementId || null,
            projectId: job.projectId || null,
            role: job.role || "",
            experienceRange: job.experienceRange || "",
            primarySkills: job.primarySkills || [null, null, null],
            secondarySkills: job.secondarySkills || [],
            budget: job.budget,
            budgetPeriod: job.budgetPeriod || "Per/month",
            location: job.reportingLocation || "",
            reportingCountry: job.reportingCountry || null,
            worldwide: job.worldwide || false,
            engagementMonths: job.monthsOfEngagement || null,
            engagementType: job.engagementType || null,
            requirementCount: job.numberOfRequirements || null,
            requirementPeriod: job.numberOfRequirementsPeriod || "Per/month",
            startDate: job.tentativeStartDate || null,
            communication: job.communication || null,
            timezone: job.workingTime || null,
            workingHours: job.workingHoursPerWeek || null,
            travel: job.travelPreference || null,
            device: job.provideSystem || null,
            tools: job.toolsUsed || [],
            shiftTiming: job.shiftTiming || null,
            shiftStart: job.shiftStart || null,
            shiftEnd: job.shiftEnd || null,
            responsibilities: job.jobResponsibilities || "",
          });
          setValue(job.jobResponsibilities || "");
        }
      } catch (error) {
        console.error("Error fetching job:", error);
        message.error("Failed to load job data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobData();
  }, [isEditMode, jobId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    Modal.confirm({
      title: "Cancel Job Posting",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to cancel?</p>
          <p style={{ color: "#666", fontSize: 13 }}>
            All unsaved changes will be lost.
          </p>
        </div>
      ),
      okText: "Yes, Cancel",
      okButtonProps: { danger: true },
      cancelText: "Continue Editing",
      onOk: () => {
        if (fromApprovalProcess) {
          navigate('/admin/approval-process', { state: { activeTab: 'Jobs' } });
        } else {
          navigate("/admin/job-requirments");
        }
      },
    });
  };

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "align",
  ];

  const steps = [
    {
      title: "Skill Required",
      content: (
        <div className="step-body">
          <JobFormStepOne formData={formData} onChange={handleChange} setIsValid={setIsValidJobDescription}/>
        </div>
      ),
    },
    {
      title: "Basic Details",
      content: (
        <div className="step-body">
          <BasicDetails
            formData={formData}
            onChange={handleChange}
            setIsValid={setIsValidBasicDetails}
            customerOrganizations={customerOrganizations}
          />
        </div>
      ),
    },
    {
      title: "Preferences",
      content: (
        <div className="step-body">
          <WorkPreferenceForm formData={formData} onChange={handleChange} setIsValid={setIsValidWorkPreference}/>
        </div>
      ),
    },
    {
      title: "Job Responsibilities",
      content: (
        <div className="step-body">
          <div className="rich-editor-container">
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Job Responsibilities *</label>
            <ReactQuill
              theme="snow"
              value={value}
              modules={modules}
              formats={formats}
              onChange={(content) => {
                setValue(content);
                handleChange("responsibilities", content);
              }}
              placeholder="Insert text here"
              style={{ minHeight: 200 }}
            />
          </div>
        </div>
      ),
    },
  ];

  const next = () => setCurrent((prev) => prev + 1);
  const prev = () => setCurrent((prev) => prev - 1);

  const handleSubmit = async () => {
    const primarySkillsFiltered = (formData.primarySkills || []).filter(s => s);
    
    const payload = {
      customerOrganization: formData.customerOrganization,
      engagementId: formData.engagementId || null,
      projectId: formData.projectId || null,
      role: formData.role,
      experienceRange: formData.experienceRange,
      primarySkills: primarySkillsFiltered,
      secondarySkills: formData.secondarySkills || [],
      budget: Number(formData.budget) || 0,
      budgetPeriod: formData.budgetPeriod || 'Per/month',
      reportingLocation: formData.location,
      reportingCountry: formData.reportingCountry || undefined,
      worldwide: Boolean(formData.worldwide),
      monthsOfEngagement: formData.engagementMonths,
      engagementType: formData.engagementType,
      numberOfRequirements: Number(formData.requirementCount) || 1,
      numberOfRequirementsPeriod: formData.requirementPeriod || 'Per/month',
      tentativeStartDate: formData.startDate || null,
      communication: formData.communication,
      workingTime: formData.timezone,
      workingHoursPerWeek: Number(formData.workingHours) || 40,
      travelPreference: formData.travel,
      provideSystem: formData.device,
      toolsUsed: formData.tools || [],
      shiftTiming: formData.shiftTiming,
      shiftStart: formData.shiftStart || null,
      shiftEnd: formData.shiftEnd || null,
      jobResponsibilities: formData.responsibilities || '',
      status: 'open',
    };
    
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      
      if (isEditMode) {
        await axios.put(`${API_CONST.UPDATE_JOB_REQUIREMENT}/${jobId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Job updated successfully!");
      } else {
        await axios.post(API_CONST.ADD_JOB_REQUIREMENTS, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Job posted successfully!");
      }
      
      navigate("/admin/job-requirments");
    } catch (error) {
      console.error("Error submitting job:", error);
      message.error(isEditMode ? "Failed to update job" : "Failed to post job");
    } finally {
      setLoading(false);
    }
  };

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  if (loading && isEditMode) {
    return (
      <PostNewJobWrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </PostNewJobWrapper>
    );
  }

  return (
    <PostNewJobWrapper>
      <div className="post-job-header">
        <h2>{isEditMode ? "Edit Job" : "Add New Job"}</h2>
        {isEditMode && (
          <span style={{ color: "#666", fontSize: 14, marginLeft: 12 }}>
            Job ID: {jobId}
          </span>
        )}
      </div>
      
      <Steps
        current={current}
        items={items}
        style={{ marginBottom: 24, marginTop: 24 }}
      />
      
      <div>{steps[current].content}</div>
      
      <div className="form-actions" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 12 }}>
          {current > 0 && (
            <Button onClick={prev}>
              Back
            </Button>
          )}
          {current < steps.length - 1 && (
            <Button type="primary" onClick={next}>
              Save & Next
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {isEditMode ? "Update Job" : "Post Job"}
            </Button>
          )}
        </div>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    </PostNewJobWrapper>
  );
};

export default PostNewJob;
